﻿import type { FlowAnswers, StepId } from "../flow/steps";
import { STEP_ORDER } from "../flow/steps";
import type { ProfileAnswers } from "../domain/answers";
import { emptyProfileAnswers } from "../domain/answers";
import type { MediaAsset } from "../domain/types";
import { toPersistedMediaAsset } from "../media/uploadService";
import { writeLocal, type LocalWriteResult } from "../storage/localWrite";

const PROGRESS_KEY = "un8.onboarding.progress.v1";
/** Pre-account draft. New key — do not overload progress.v1. */
const DRAFT_KEY = "un8.onboarding.draft.v1";

export interface OnboardingProgress {
  userId: string;
  stepIndex: number;
  /** Written from 2026-09-10 so a shorter STEP_ORDER does not remapped new saves. */
  stepId?: StepId;
  answers: FlowAnswers;
  /** Additive questionnaire — missing on older saves → emptyProfileAnswers(). */
  profileAnswers?: ProfileAnswers;
  updatedAt: string;
}

type ProgressMap = Record<string, OnboardingProgress>;

function readAll(): ProgressMap {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as ProgressMap;
  } catch {
    return {};
  }
}

function writeAll(map: ProgressMap): LocalWriteResult {
  return writeLocal(PROGRESS_KEY, JSON.stringify(map));
}

function persistAsset(asset: MediaAsset | null): MediaAsset | null {
  if (!asset) return null;
  return toPersistedMediaAsset(asset);
}

/** Drop secrets + ephemeral blob URLs; keep durable storageKey refs. */
export function sanitizeAnswersForStorage(answers: FlowAnswers): FlowAnswers {
  return {
    ...answers,
    password: "",
    country: answers.country ?? "",
    preferredPartnerCountry: answers.preferredPartnerCountry ?? "any",
    governmentId: persistAsset(answers.governmentId),
    confirmationVideo: persistAsset(answers.confirmationVideo),
    profilePhotos: answers.profilePhotos.map((p) => toPersistedMediaAsset(p)),
  };
}

/** Fill fields added after older drafts were saved. */
export function hydrateFlowAnswers(answers: FlowAnswers): FlowAnswers {
  const scales = answers.scales ?? {};
  return {
    ...answers,
    country: answers.country ?? "",
    preferredPartnerCountry: answers.preferredPartnerCountry ?? "any",
    scales: {
      loyalty: scales.loyalty ?? null,
      honesty: scales.honesty ?? null,
      passion: scales.passion ?? null,
      commitment: scales.commitment ?? null,
      social: scales.social ?? null,
      creativity: scales.creativity ?? null,
      humor: scales.humor ?? null,
    },
  };
}


// Where a saved step goes when that step is no longer in STEP_ORDER.
// Updated 11 Sep 2026: the nine-step cut removed communityRules, whyChristian,
// lifestyle and the whole verifyIntro / verifyId / verifyVideo / verifyReview
// block. Without an entry here those saves fall through to a numeric guess.
const LEFT_PATH: Partial<Record<StepId, StepId>> = {
  setupIntro: "aboutYou",
  communityRules: "intent",
  whyChristian: "aboutYou",
  lifestyle: "profile",
  verifyIntro: "verifyPhotos",
  verifyId: "verifyPhotos",
  verifyVideo: "verifyPhotos",
  verifyReview: "verifyPhotos",
  faithDepth: "profile",
  character: "profile",
  history: "profile",
  appearance: "profile",
  homeAndLand: "profile",
  family: "profile",
  ancestry: "profile",
  interests: "profile",
  reflection: "profile",
};

function migrateStepIndex(index: number): number {
  // Pre-2026-09-10: 10 setupIntro, 11–16 prompts, 17–25 deep survey,
  // 26 profile, 27 ready. setupIntro and the deep survey left the path.
  if (index === 10) return STEP_ORDER.indexOf("whyChristian");
  if (index >= 11 && index <= 16) return index - 1;
  if (index >= 17 && index <= 26) return STEP_ORDER.indexOf("profile");
  if (index >= 27) return STEP_ORDER.indexOf("ready");
  return index;
}

function resolveStepIndex(row: OnboardingProgress, answers: FlowAnswers): number {
  if (row.stepId) {
    const mapped = LEFT_PATH[row.stepId] ?? row.stepId;
    const byId = STEP_ORDER.indexOf(mapped);
    if (byId >= 0) return clampStepIndex(byId, answers);
  }
  return clampStepIndex(migrateStepIndex(row.stepIndex), answers);
}

function clampStepIndex(index: number, answers: FlowAnswers): number {
  const i = Math.max(0, Math.min(index, STEP_ORDER.length - 1));
  // 11 Sep 2026: ID and video left onboarding, so nothing rewinds a person to a
  // verification step any more. The old body looked up "verifyReview" and
  // "verifyIntro"; both are gone from STEP_ORDER, indexOf returned -1, and this
  // function handed back step -1. The only media step left is verifyPhotos and it
  // asks for a single picture, so that is the only thing worth guarding.
  const photos = STEP_ORDER.indexOf("verifyPhotos");
  if (photos >= 0 && i > photos && answers.profilePhotos.length === 0) {
    return photos;
  }
  return i;
}

/**
 * Per-user onboarding progress (answers + step), keyed by stable auth user id.
 * Storage key stays `un8.onboarding.progress.v1` — do not rename.
 */
export const onboardingStore = {
  load(userId: string): OnboardingProgress | null {
    const row = readAll()[userId];
    if (!row) return null;
    const answers = hydrateFlowAnswers({ ...row.answers, password: "" });
    return {
      ...row,
      answers,
      profileAnswers: row.profileAnswers ?? emptyProfileAnswers(),
      stepIndex: resolveStepIndex(row, answers),
    };
  },

  save(
    userId: string,
    stepIndex: number,
    answers: FlowAnswers,
    profileAnswers: ProfileAnswers
  ): LocalWriteResult {
    const map = readAll();
    map[userId] = {
      userId,
      stepIndex,
      stepId: STEP_ORDER[stepIndex],
      answers: sanitizeAnswersForStorage(answers),
      profileAnswers,
      updatedAt: new Date().toISOString(),
    };
    return writeAll(map);
  },

  saveDraft(
    stepIndex: number,
    answers: FlowAnswers,
    profileAnswers: ProfileAnswers
  ): LocalWriteResult {
    const row: OnboardingProgress = {
      userId: "draft",
      stepIndex,
      stepId: STEP_ORDER[stepIndex],
      answers: sanitizeAnswersForStorage(answers),
      profileAnswers,
      updatedAt: new Date().toISOString(),
    };
    return writeLocal(DRAFT_KEY, JSON.stringify(row));
  },

  loadDraft(): OnboardingProgress | null {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const row = JSON.parse(raw) as OnboardingProgress;
      if (!row?.answers) return null;
      const answers = hydrateFlowAnswers({ ...row.answers, password: "" });
      return {
        ...row,
        answers,
        profileAnswers: row.profileAnswers ?? emptyProfileAnswers(),
        stepIndex: resolveStepIndex(row, answers),
      };
    } catch {
      return null;
    }
  },

  clearDraft(): void {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {
      /* ignore */
    }
  },

  clear(userId: string): void {
    const map = readAll();
    delete map[userId];
    writeAll(map);
  },
};
